import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';

interface Ctx {
  isMobile: boolean;
  open: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
}
const CtxObj = createContext<Ctx | null>(null);
export const MobileSearchProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const mq = useMemo(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 760px)') : null),
    [],
  );
  const [isMobile, setIsMobile] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [open, setOpen] = useState(false);
  // keep isMobile in sync
  useEffect(() => {
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mq]);
  // lock body when overlay is open
  useEffect(() => {
    if (!isMobile) return;
    const { body } = document;
    if (open) {
      body.classList.add('mobile-search-open');
      body.style.overflow = 'hidden';
      body.style.height = '100%';
    } else {
      body.classList.remove('mobile-search-open');
      body.style.overflow = '';
      body.style.height = '';
    }
    return () => {
      body.classList.remove('mobile-search-open');
      body.style.overflow = '';
      body.style.height = '';
    };
  }, [open, isMobile]);
  const value: Ctx = {
    isMobile,
    open,
    openSearch: () => setOpen(true),
    closeSearch: () => setOpen(false),
    toggleSearch: () => setOpen((v) => !v),
  };
  return <CtxObj.Provider value={value}>{children}</CtxObj.Provider>;
};
export const useMobileSearch = () => {
  const ctx = useContext(CtxObj);
  if (!ctx) throw new Error('useMobileSearch must be used within MobileSearchProvider');
  return ctx;
};
